import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollProgressOptions {
  /** Throttle interval in ms (default: 16 — ~60fps) */
  throttleMs?: number;
}

interface ScrollProgressResult {
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement>;
  /** 0–100 scroll progress percentage */
  progress: number;
  /** Whether the container is scrollable at all */
  isScrollable: boolean;
  /** Whether user has reached the bottom */
  isAtBottom: boolean;
  /** Remaining items count (approximate) */
  remainingRatio: number;
}

/**
 * Tracks scroll progress inside a scrollable container.
 * Returns a 0-100 progress value and helper flags.
 */
export function useScrollProgress(options: UseScrollProgressOptions = {}): ScrollProgressResult {
  const { throttleMs = 16 } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const lastCallRef = useRef(0);

  const update = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const now = Date.now();
    if (now - lastCallRef.current < throttleMs) return;
    lastCallRef.current = now;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    const scrollable = maxScroll > 10;
    setIsScrollable(scrollable);

    if (!scrollable) {
      setProgress(100);
      return;
    }

    const pct = Math.min(100, Math.round((scrollTop / maxScroll) * 100));
    setProgress(pct);
  }, [throttleMs]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Initial check
    update();

    el.addEventListener('scroll', update, { passive: true });
    // Also observe resize
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [update]);

  return {
    containerRef,
    progress,
    isScrollable,
    isAtBottom: progress >= 98,
    remainingRatio: 1 - progress / 100,
  };
}